import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  BellRing,
  CheckCircle2,
  Edit3,
  ExternalLink,
  Mail,
  MessageCircle,
  MoreVertical,
  Share2,
  StickyNote,
  AlertCircle,
  FileText,
  IndianRupee,
  ReceiptIndianRupee,
} from "lucide-react";
import { AES, enc } from "crypto-js";
import QRCode from "qrcode.react";
import swal from "sweetalert";
import PremiumListPage, { StatusBadge } from "../../../Components/Dashboard/PremiumListPage";
import SearchBar from "../../../Components/Common/SearchBar";
import InvoiceUpdate from "../SalesNew/InvoicesUpdate";
import { BASE_URL } from "../CommonApi";
import DocumentPrintActions from "../../../Components/documents/print/DocumentPrintActions";
import {
  buildInvoicePrintModel,
  cleanInvoiceValue,
  formatInvoiceMoney,
  getInvoiceStatus,
} from "../../../utils/invoice/invoiceFormatter";
import "./ModernInvoiceWorkspace.css";

const decodeUniqueId = () => AES.decrypt(localStorage.getItem("uniqueId1"), "1234567812345678").toString(enc.Utf8);

const normalizeRows = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];
  return Object.values(value).find(Array.isArray) || [];
};

const money = formatInvoiceMoney;
const formatText = cleanInvoiceValue;
const invoiceStatus = getInvoiceStatus;

const initials = (name = "") =>
  String(name || "IN")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase() || "IN";

const invoiceIdForUrl = (invoiceNumber = "") => encodeURIComponent(String(invoiceNumber).replace(/\//g, "!"));

const statusBadgeTone = (invoice = {}) => {
  const tone = invoiceStatus(invoice).tone;
  if (tone === "paid") return "green";
  if (tone === "overdue") return "red";
  return "orange";
};

function Invoices({ navigation }) {
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoiceNumber, setSelectedInvoiceNumber] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState({});
  const [customerData, setCustomerData] = useState({});
  const [userDetails, setUserDetails] = useState({});
  const [bankData, setBankData] = useState({});
  const [gstData, setGstData] = useState({});
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [showWorkspace, setShowWorkspace] = useState(false);

  const jwtToken = localStorage.getItem("jwtToken");

  const authedFetch = (endpoint, body = {}) =>
    fetch(BASE_URL + endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwtToken}`,
      },
      body: JSON.stringify({ uniqueId: decodeUniqueId(), ...body }),
    }).then((response) => response.json());

  const fetchInvoices = () => {
    setLoading(true);
    authedFetch("/fetchInvoice")
      .then((data) => {
        const nextRows = data.status === "success" ? normalizeRows(data.responseMessage) : [];
        setInvoices(nextRows);
        setSelectedInvoiceNumber((current) => current || nextRows[0]?.invoiceNumber || "");
      })
      .catch((error) => console.error("Error fetching invoices:", error))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchInvoices();

    const userId = localStorage.getItem("userId");
    authedFetch("/fetchUserProfile", { userId })
      .then((data) => data.status === "success" && setUserDetails(data.responseMessage || {}))
      .catch((error) => console.error("Error fetching profile:", error));

    authedFetch("/fetchBankForPdf")
      .then(async (data) => {
        const primaryBank = data.status === "success" ? data.responseMessage || {} : {};
        if (primaryBank.bankName || primaryBank.accountNumber || primaryBank.upiId) {
          setBankData(primaryBank);
          return;
        }

        const allBanksResponse = await authedFetch("/fetchBanks");
        const banks = normalizeRows(allBanksResponse.responseMessage);
        setBankData(
          banks.find((item) => String(item.primaryAccount).toUpperCase() === "YES") ||
          banks[0] ||
          {},
        );
      })
      .catch((error) => console.error("Error fetching bank:", error));

    authedFetch("/fetchGst")
      .then((data) => setGstData(normalizeRows(data.responseMessage)[0] || {}))
      .catch((error) => console.error("Error fetching GST:", error));
  }, []);

  useEffect(() => {
    if (!selectedInvoiceNumber) return;
    setDetailLoading(true);
    authedFetch(`/fetchInvoiceNumber/${invoiceIdForUrl(selectedInvoiceNumber)}`)
      .then((data) => {
        const rows = data.status === "success" ? normalizeRows(data.responseMessage) : [];
        const firstRow = rows[0] || invoices.find((invoice) => invoice.invoiceNumber === selectedInvoiceNumber) || {};
        setSelectedRows(rows);
        setSelectedInvoice(firstRow);

        const customerId = firstRow.customerId || invoices.find((invoice) => invoice.invoiceNumber === selectedInvoiceNumber)?.customerId;
        if (customerId) {
          authedFetch(`/fetchCustomerById/${customerId}`)
            .then((customerResponse) => customerResponse.status === "success" && setCustomerData(customerResponse.responseMessage || {}))
            .catch((error) => console.error("Error fetching customer:", error));
        } else {
          setCustomerData({});
        }
      })
      .catch((error) => console.error("Error fetching invoice detail:", error))
      .finally(() => setDetailLoading(false));
  }, [selectedInvoiceNumber]);

  const filteredInvoices = useMemo(() => {
    const search = query.trim().toLowerCase();
    return invoices.filter((invoice) => {
      const status = invoiceStatus(invoice).tone;
      const matchesStatus = statusFilter === "all" || status === statusFilter;
      const matchesSearch =
        !search ||
        [invoice.customerName, invoice.invoiceNumber, invoice.total, invoice.invoiceDate, invoice.dueDate]
          .some((value) => String(value || "").toLowerCase().includes(search));
      return matchesStatus && matchesSearch;
    });
  }, [invoices, query, statusFilter]);

  const statusCounts = useMemo(
    () => ({
      all: invoices.length,
      paid: invoices.filter((invoice) => invoiceStatus(invoice).tone === "paid").length,
      pending: invoices.filter((invoice) => invoiceStatus(invoice).tone === "pending").length,
      overdue: invoices.filter((invoice) => invoiceStatus(invoice).tone === "overdue").length,
    }),
    [invoices],
  );

  const itemRows = useMemo(
    () => (selectedRows.length ? selectedRows : selectedInvoice.invoiceNumber ? [selectedInvoice] : []),
    [selectedRows, selectedInvoice],
  );
  const currentStatus = useMemo(() => invoiceStatus(selectedInvoice), [selectedInvoice]);
  const logoUrl = userDetails.logo ? `https://documents.zarpsoftware.in/zarp_images/${userDetails.logo}` : "";
  const paymentUrl = `upi://pay?pa=${bankData.upiId || ""}&am=${selectedInvoice.total || 0}&cu=INR`;
  const companyGstin = gstData.businessRegistered === "YES" ? gstData.gstin : "";
  const isGstEnabled = gstData.businessRegistered === "YES" && Boolean(companyGstin);
  const printInvoice = useMemo(
    () =>
      buildInvoicePrintModel({
        selectedInvoice,
        itemRows,
        customerData,
        userDetails,
        gstData,
        bankData,
        status: currentStatus,
      }),
    [selectedInvoice, itemRows, customerData, userDetails, gstData, bankData, currentStatus],
  );

  const buildPdfPayload = () => ({
    total: selectedInvoice.total,
    invoiceNumber: selectedInvoice.invoiceNumber,
    invoiceDate: selectedInvoice.invoiceDate,
    dueDate: selectedInvoice.dueDate,
    organizationName: userDetails.companyName,
    custmorName: selectedInvoice.customerName || customerData.customerName,
    companyName: userDetails.companyName,
    terms: selectedInvoice.paymentTerms,
    items: itemRows.map((item) => ({
      items: item.itemsDetails || "",
      quantity: item.quantity || "",
      rate: item.rate || "",
      discount: item.discount || "",
      amount: item.amount || "",
      discountType: item.discountType || "",
      size: item.size || "",
      taxRate: item.tax || "",
      hsnCode: item.hsnCode || "",
    })),
    name: bankData.name,
    branch: bankData.bankName,
    accountNo: bankData.accountNumber,
    ifscCode: bankData.ifsc,
    bankCheck: bankData.primaryAccount,
    type: bankData.accountType,
    totalInWords: selectedInvoice.amountInWords || "",
    subTotal: selectedInvoice.subtotal,
    adjustment: selectedInvoice.adjustment,
    totalDue: selectedInvoice.receivables,
    notes: selectedInvoice.customerNote,
    state: selectedInvoice.state,
    address: userDetails.address1,
    imageName: userDetails.logo,
    uniqueId: decodeUniqueId(),
    sizeColumn: selectedInvoice.sizeCheck,
    discountColumn: selectedInvoice.discountCheck,
    bankColumn: bankData.upiId ? "YES" : "NO",
    taxColumn: selectedInvoice.taxCheck,
    igst: selectedInvoice.igst,
    cgst: selectedInvoice.cgst,
    sgst: selectedInvoice.sgst,
    orderNo: selectedInvoice.referenceNumber,
    orgMob1: userDetails.contact,
    organizationStreetName: userDetails.address1,
    organizationAddress: userDetails.address2,
    organizationPinCode: userDetails.zipcode,
    custmorAddress: customerData.billAddress1,
    custmorCity: customerData.billCity,
    custmorMob: customerData.billPhone,
    termsAndCondition: selectedInvoice.termsAndCondition,
    organizationGSTNo: companyGstin,
    templateFormat: "default",
  });

  const sendMail = async () => {
    try {
      const response = await fetch(BASE_URL + "/send-invoice-email-with-pdf", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({
          ...buildPdfPayload(),
          to: customerData.email,
          from: userDetails.email,
          subject: `Invoice ${selectedInvoice.invoiceNumber}`,
          body: `Dear ${selectedInvoice.customerName || customerData.customerName}, please find your invoice attached.`,
        }),
      });
      const data = await response.json();
      swal({
        icon: data.status === "success" ? "success" : "error",
        title: data.status === "success" ? "Mail Sent Successfully" : data.responseMessage || "Unable to send mail",
      });
    } catch (error) {
      swal({ icon: "error", title: error.message });
    }
  };

  const shareInvoice = () => {
    const title = selectedInvoice.invoiceNumber || "Invoice";
    const text = `${title} for ${selectedInvoice.customerName || customerData.customerName || ""} - ${money(selectedInvoice.total)}`;
    if (navigator.share) {
      navigator.share({ title, text }).catch(() => {});
    } else {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text);
      }
      swal({ icon: "success", title: "Invoice details copied" });
    }
  };

  const openInvoiceWorkspace = (invoice) => {
    setSelectedInvoiceNumber(invoice.invoiceNumber);
    setShowWorkspace(true);
  };

  const deleteInvoice = (invoice) => {
    if (!window.confirm("Are you sure? Once deleted, you will not be able to recover this invoice!")) return;

    fetch(BASE_URL + `/deleteByInvoiceNumber/${invoiceIdForUrl(invoice.invoiceNumber)}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwtToken}`,
      },
      body: JSON.stringify({ uniqueId: decodeUniqueId() }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === "success") {
          setInvoices((current) => current.filter((row) => row.invoiceNumber !== invoice.invoiceNumber));
          swal({ icon: "success", title: "Invoice Deleted Successfully" });
        } else if (data.responseMessage) {
          swal({ icon: "error", title: data.responseMessage });
        }
      })
      .catch((error) => console.error("Error deleting invoice:", error));
  };

  if (editInvoice) {
    return (
      <InvoiceUpdate
        invoiceNumber1={editInvoice.invoiceNumber}
        fetchedStatus={editInvoice.status}
        handleCancel={() => {
          setEditInvoice(null);
          fetchInvoices();
        }}
      />
    );
  }

  if (!showWorkspace) {
    if (loading) return <div className="premium-empty-state">Loading invoices...</div>;

    const totalAmount = invoices.reduce((sum, invoice) => sum + Number(invoice.total || 0), 0);
    const paidAmount = invoices
      .filter((invoice) => invoiceStatus(invoice).tone === "paid")
      .reduce((sum, invoice) => sum + Number(invoice.total || 0), 0);
    const overdueCount = invoices.filter((invoice) => invoiceStatus(invoice).tone === "overdue").length;

    return (
      <PremiumListPage
        title="Invoices"
        subtitle="Manage and track all your invoices in one place."
        actionLabel="+ New Invoice"
        onCreate={() => navigation.navigate("InvoicesNew")}
        searchPlaceholder="Search invoices..."
        rows={invoices}
        filterFields={[
          {
            key: "invoiceStatus",
            label: "Status",
            getValue: (row) => invoiceStatus(row).tone,
            options: [
              { value: "paid", label: "Paid" },
              { value: "pending", label: "Pending" },
              { value: "overdue", label: "Overdue" },
            ],
          },
        ]}
        stats={[
          { label: "Total Invoices", value: invoices.length, icon: FileText, tone: "blue" },
          { label: "Total Amount", value: money(totalAmount), icon: IndianRupee, tone: "orange" },
          { label: "Paid Amount", value: money(paidAmount), icon: ReceiptIndianRupee, tone: "green" },
          { label: "Overdue Invoices", value: overdueCount, icon: AlertCircle, tone: "red" },
        ]}
        columns={[
          { key: "invoiceDate", label: "Date" },
          {
            key: "invoiceNumber",
            label: "Invoice #",
            render: (row) => (
              <button className="premium-link" onClick={() => openInvoiceWorkspace(row)}>
                {row.invoiceNumber || "-"}
              </button>
            ),
          },
          { key: "orderNumber", label: "Order Number" },
          { key: "customerName", label: "Customer Name" },
          {
            key: "status",
            label: "Status",
            render: (row) => <StatusBadge tone={statusBadgeTone(row)}>{invoiceStatus(row).label}</StatusBadge>,
          },
          { key: "dueDate", label: "Due Date" },
          { key: "total", label: "Amount", className: "premium-money", render: (row) => money(row.total) },
        ]}
        onView={openInvoiceWorkspace}
        onEdit={(row) => setEditInvoice(row)}
        onDelete={deleteInvoice}
      />
    );
  }

  return (
    <section className="invoice-workspace">
      <aside className="invoice-list-panel">
        <div className="invoice-list-head">
          <button className="invoice-back-btn" onClick={() => setShowWorkspace(false)} aria-label="Back to invoice list">
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1>Invoices</h1>
            <p>{invoices.length} invoices</p>
          </div>
        </div>

        <div className="invoice-list-tools">
          <SearchBar value={query} onChange={setQuery} placeholder="Search invoices..." className="invoice-list-search" />
        </div>

        <div className="invoice-status-tabs">
          {[
            ["all", "All"],
            ["paid", "Paid"],
            ["pending", "Pending"],
            ["overdue", "Overdue"],
          ].map(([key, label]) => (
            <button key={key} className={statusFilter === key ? "is-active" : ""} onClick={() => setStatusFilter(key)}>
              {label} <span>{statusCounts[key]}</span>
            </button>
          ))}
        </div>

        <div className="invoice-list-scroll">
          {loading && <div className="invoice-empty">Loading invoices...</div>}
          {!loading && filteredInvoices.length === 0 && <div className="invoice-empty">No invoices found.</div>}
          {filteredInvoices.map((invoice) => {
            const status = invoiceStatus(invoice);
            const active = invoice.invoiceNumber === selectedInvoiceNumber;
            return (
              <button
                key={invoice.invoiceNumber || invoice.id}
                className={`invoice-card-row ${active ? "is-active" : ""}`}
                onClick={() => setSelectedInvoiceNumber(invoice.invoiceNumber)}
              >
                <span className={`invoice-avatar ${status.tone}`}>{initials(invoice.customerName)}</span>
                <span className="invoice-row-main">
                  <strong>{formatText(invoice.customerName, "Unknown customer")}</strong>
                  <small>{formatText(invoice.invoiceNumber)}</small>
                  <b>{money(invoice.total)}</b>
                </span>
                <span className="invoice-row-side">
                  <time>{formatText(invoice.invoiceDate || invoice.dueDate)}</time>
                  <span className={`invoice-pill ${status.tone}`}>{status.label}</span>
                </span>
              </button>
            );
          })}
        </div>
      </aside>

      <main className="invoice-detail-panel">
        {detailLoading && <div className="invoice-detail-loading">Loading invoice detail...</div>}
        {!detailLoading && !selectedInvoice.invoiceNumber && <div className="invoice-detail-loading">Select an invoice to preview.</div>}
        {!detailLoading && selectedInvoice.invoiceNumber && (
          <>
            <div className="invoice-detail-top">
              <div>
                <div className="invoice-title-row">
                  <h2>{selectedInvoice.invoiceNumber}</h2>
                  <span className={`invoice-pill ${currentStatus.tone}`}>{currentStatus.label}</span>
                </div>
                <p>
                  Invoice for <strong>{formatText(selectedInvoice.customerName || customerData.customerName, "Customer")}</strong>
                </p>
              </div>
              <div className="invoice-actions">
                <button onClick={() => setEditInvoice(selectedInvoice)}><Edit3 size={16} /> Edit</button>
                <button onClick={sendMail}><Mail size={16} /> Send Mail</button>
                <button className="is-icon-only" title="WhatsApp" aria-label="WhatsApp"><MessageCircle size={16} /></button>
                <button className="is-icon-only" title="Reminder" aria-label="Reminder"><BellRing size={16} /></button>
                <DocumentPrintActions
                  documentModel={printInvoice}
                  documentType="invoice"
                  onError={(message) => swal({ icon: "error", title: message })}
                />
                <button className="is-icon-only" title="Payment Link" aria-label="Payment Link"><ExternalLink size={16} /></button>
                <button className="is-icon-only" title="Notes" aria-label="Notes"><StickyNote size={16} /></button>
                <button onClick={shareInvoice}><Share2 size={16} /> Share</button>
                <button className="is-icon-only" aria-label="More"><MoreVertical size={16} /></button>
              </div>
            </div>

            <div className={`invoice-payment-card ${currentStatus.tone}`}>
              <CheckCircle2 size={22} />
              <div>
                <strong>
                  {currentStatus.tone === "paid"
                    ? `Paid on ${formatText(selectedInvoice.paymentDate || selectedInvoice.invoiceDate)}`
                    : `${currentStatus.label} invoice`}
                </strong>
                <span>
                  {currentStatus.tone === "paid"
                    ? `Payment of ${money(selectedInvoice.total)} received successfully`
                    : `${money(selectedInvoice.receivables || selectedInvoice.total)} pending against this invoice`}
                </span>
              </div>
              <button onClick={() => navigation.navigate("Payment")}>View Payments</button>
            </div>

            <div className="invoice-detail-grid">
              <section className="invoice-preview-card" id="modern-invoice-preview">
                <div className="invoice-preview-head">
                  <div className="invoice-company-block">
                    {logoUrl ? <img src={logoUrl} alt={userDetails.companyName || "Company logo"} /> : <span>{initials(userDetails.companyName)}</span>}
                    <div>
                      <h3>{formatText(userDetails.companyName, "Company")}</h3>
                      <p>{[userDetails.address1, userDetails.address2, userDetails.city, userDetails.zipcode].filter(Boolean).join(", ")}</p>
                      <p>{isGstEnabled ? `GSTIN: ${companyGstin}` : ""}</p>
                      <p>{userDetails.contact ? `Phone: ${userDetails.contact}` : ""}</p>
                    </div>
                  </div>
                  <div className="invoice-tax-title">
                    <h3>{isGstEnabled ? "TAX INVOICE" : "INVOICE"}</h3>
                    <QRCode value={paymentUrl} size={82} />
                  </div>
                </div>

                <div className="invoice-bill-strip">
                  <div className="is-wide">
                    <span>Bill To</span>
                    <strong>{formatText(selectedInvoice.customerName || customerData.customerName)}</strong>
                    <p>{[customerData.billAddress1, customerData.billCity, customerData.billState, customerData.billPincode].filter(Boolean).join(", ") || "-"}</p>
                  </div>
                  {isGstEnabled && (
                    <div>
                      <span>GSTIN</span>
                      <strong>{formatText(customerData.gstin || customerData.gstNumber)}</strong>
                    </div>
                  )}
                  <div>
                    <span>Phone</span>
                    <strong>{formatText(customerData.billPhone || customerData.phone)}</strong>
                  </div>
                  <div>
                    <span>Email</span>
                    <strong>{formatText(customerData.email)}</strong>
                  </div>
                </div>

                <div className="invoice-preview-meta">
                  <div>
                    <span>Invoice No</span>
                    <strong>{formatText(selectedInvoice.invoiceNumber)}</strong>
                  </div>
                  <div>
                    <span>Invoice Date</span>
                    <strong>{formatText(selectedInvoice.invoiceDate)}</strong>
                  </div>
                  <div>
                    <span>Due Date</span>
                    <strong>{formatText(selectedInvoice.dueDate)}</strong>
                  </div>
                  <div>
                    <span>Terms</span>
                    <strong>{formatText(selectedInvoice.paymentTerms || selectedInvoice.terms, "Net 0")}</strong>
                  </div>
                </div>

                <div className="invoice-items-table">
                  <table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Item & Description</th>
                        {isGstEnabled && <th>HSN/SAC</th>}
                        <th>Qty</th>
                        <th>Rate</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itemRows.map((item, index) => (
                        <tr key={`${item.itemsDetails || item.itemName || index}-${index}`}>
                          <td>{index + 1}</td>
                          <td>{formatText(item.itemsDetails || item.itemName || item.name)}</td>
                          {isGstEnabled && <td>{formatText(item.hsnCode || item.hsnSac)}</td>}
                          <td>{formatText(item.quantity)}</td>
                          <td>{money(item.rate)}</td>
                          <td>{money(item.amount || Number(item.quantity || 0) * Number(item.rate || 0))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="invoice-preview-bottom">
                  <article>
                    <h4>Amount in Words</h4>
                    <p>{formatText(selectedInvoice.amountInWords, "Available on generated PDF")}</p>
                    <h4>Notes</h4>
                    <p>{formatText(selectedInvoice.customerNote, "Thank you for your business.")}</p>
                  </article>
                  <article className="invoice-total-card">
                    <div><span>Sub Total</span><strong>{money(selectedInvoice.subtotal || selectedInvoice.total)}</strong></div>
                    {isGstEnabled && <div><span>CGST</span><strong>{money(selectedInvoice.cgst)}</strong></div>}
                    {isGstEnabled && <div><span>SGST</span><strong>{money(selectedInvoice.sgst)}</strong></div>}
                    {isGstEnabled && <div><span>IGST</span><strong>{money(selectedInvoice.igst)}</strong></div>}
                    <div className="grand-total"><span>Total Amount</span><strong>{money(selectedInvoice.total)}</strong></div>
                    <span className={`invoice-pill ${currentStatus.tone}`}>{currentStatus.label}</span>
                  </article>
                </div>
              </section>
            </div>
          </>
        )}
      </main>
    </section>
  );
}

export default Invoices;
